import { api, apiRedirectJson } from "../client";
import type {
  ActivityDetailRow,
  ActivityRow,
  AnnualSummaryRow,
  Coordinates,
  LapRow,
  RawSample,
  TotalSummaryRow,
  UploadUrl,
  Visibility,
} from "../types";

type Listed<T> = { status: string; message: string; data: T };
type Mutated = { status: string; message: string; synchUpdated: number };

export const activityApi = {
  list: () => api<Listed<ActivityRow[]>>("activity/activitylist").then((r) => r.data ?? []),
  get: (activityid: string) =>
    api<Listed<ActivityDetailRow[]>>("activity/activity", { body: { activityid }, ignoreAuthFailure: true }).then(
      (r) => (Array.isArray(r.data) ? r.data[0] : (r.data as unknown as ActivityDetailRow)) ?? null,
    ),
  samples: async (activityid: string): Promise<RawSample[]> => {
    const j = await apiRedirectJson<RawSample[] | { data: RawSample[] }>("activity/activitydata", { activityid }, {
      ignoreAuthFailure: true,
    });
    return Array.isArray(j) ? j : (j?.data ?? []);
  },
  laps: (activityid: string) =>
    api<Listed<LapRow[]>>("activity/laps", { body: { activityid }, ignoreAuthFailure: true }).then((r) => r.data ?? []),
  coordinates: (activityid: string) =>
    api<Listed<Coordinates>>("activity/coordinates", { body: { activityid } }).then((r) => r.data),
  update: (activityid: string, patch: { description?: string; visibility?: Visibility }) =>
    api<Mutated>("activity/update", { body: { activityid, ...patch } }),
  delete: (activityid: string) => api<Mutated>("activity/delete", { body: { activityid } }),
  reparse: (opts: { activityid?: string; nomap?: 1; nocoord?: 1; rulesonly?: 1 } = {}) =>
    api<{ status: string; message: string }>("activity/reparse", { body: opts }),
  setTags: (activityid: string, tags: number[]) =>
    api<Mutated & { tags: number[] }>("activity/setTags", { body: { activityid, tags } }),
  setPbIgnored: (activityid: string, distance: number, ignored: boolean) =>
    api<{ status: string; message: string }>(ignored ? "activity/enablepb" : "activity/disablepb", {
      body: { activityid, distance },
    }),
  annualSummaries: () => api<Listed<AnnualSummaryRow[]>>("activity/annualsummaries").then((r) => r.data ?? []),
  totalSummary: () => api<Listed<TotalSummaryRow[]>>("activity/totalsummary").then((r) => r.data ?? []),
  uploadUrl: (filename: string) => api<UploadUrl & { status: string }>("activity/upload-url", { body: { filename } }),
  confirmUpload: (s3Key: string, originalFilename: string, deleteDuplicate: boolean) =>
    api<{ status: string; message: string }>("activity/confirm-upload", {
      body: { s3Key, originalFilename, deleteDuplicate },
    }),
};
